/**
 * MafiaRoleReminder — Persistent role badge with contextual phase prompts
 *
 * Requirements: 32.1–32.5
 */

import React, { useState, useEffect } from 'react';
import { MafiaRole, Team, GamePhaseType, ROLE_INFO, InvestigationResult } from '../types';
import './MafiaRoleReminder.css';

export interface MafiaRoleReminderProps {
  role: MafiaRole;
  team: Team;
  currentPhase: GamePhaseType;
  mafiaMembers?: string[];
  investigationResults?: InvestigationResult[];
  lastProtectedPlayer?: string;
}

const PHASE_PROMPTS: Record<string, Record<MafiaRole, string>> = {
  night: {
    mafia: 'Choose a player to eliminate...',
    doctor: 'Choose a player to protect...',
    detective: 'Choose a player to investigate...',
    villager: 'The village sleeps...',
  },
  dayDiscussion: {
    mafia: 'Discuss with other players...',
    doctor: 'Discuss with other players...',
    detective: 'Discuss with other players...',
    villager: 'Discuss with other players...',
  },
  dayVoting: {
    mafia: 'Vote for the player you want to eliminate',
    doctor: 'Vote for the player you want to eliminate',
    detective: 'Vote for the player you want to eliminate',
    villager: 'Vote for the player you want to eliminate',
  },
};

export const MafiaRoleReminder: React.FC<MafiaRoleReminderProps> = ({
  role,
  team,
  currentPhase,
  mafiaMembers,
  investigationResults,
  lastProtectedPlayer,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [lastPhase, setLastPhase] = useState<string>('');

  const roleInfo = ROLE_INFO[role];

  // Show phase prompt when phase changes
  useEffect(() => {
    if (currentPhase !== lastPhase && currentPhase !== 'lobby' && currentPhase !== 'roleReveal' && currentPhase !== 'gameOver') {
      setLastPhase(currentPhase);
      setShowPrompt(true);
      const timer = setTimeout(() => setShowPrompt(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, lastPhase]);

  const handlePromptDismiss = () => setShowPrompt(false);

  const handleBadgeClick = () => {
    setShowDetail(!showDetail);
    setShowPrompt(false);
  };

  const phasePrompt = PHASE_PROMPTS[currentPhase]?.[role];

  return (
    <div className="mafia-role-reminder">
      {showPrompt && phasePrompt && (
        <div
          className="mafia-role-reminder__phase-prompt"
          onClick={handlePromptDismiss}
          role="status"
          aria-live="polite"
        >
          {phasePrompt}
        </div>
      )}

      {showDetail && (
        <div className="mafia-role-reminder__detail" role="tooltip">
          <h4>{roleInfo.icon} {roleInfo.name}</h4>
          <p><strong>Team:</strong> {team === 'village' ? 'Village' : 'Mafia'}</p>
          <p>{roleInfo.description}</p>

          {role === 'mafia' && mafiaMembers && mafiaMembers.length > 0 && (
            <div className="mafia-role-reminder__detail-info">
              <strong>Fellow Mafia:</strong> {mafiaMembers.join(', ')}
            </div>
          )}

          {role === 'detective' && investigationResults && investigationResults.length > 0 && (
            <div className="mafia-role-reminder__detail-info">
              <strong>Investigations:</strong>
              {investigationResults.map((r, i) => (
                <div key={i}>{r.targetName}: {r.isMafia ? '🔪 Mafia' : '✅ Not Mafia'}</div>
              ))}
            </div>
          )}

          {role === 'doctor' && lastProtectedPlayer && (
            <div className="mafia-role-reminder__detail-info">
              <strong>Last protected:</strong> {lastProtectedPlayer}
            </div>
          )}
        </div>
      )}

      <button
        className={`mafia-role-reminder__badge mafia-role-reminder__badge--${team}`}
        onClick={handleBadgeClick}
        aria-label={`My Role: ${roleInfo.name} (${team === 'village' ? 'Village' : 'Mafia'} team)`}
      >
        <span>{roleInfo.icon}</span>
        <span>{roleInfo.name}</span>
      </button>
    </div>
  );
};

export default MafiaRoleReminder;
